/**
 * Turning an auth failure into something the person in front of the screen can act on.
 *
 * ── Why the server's message is not always the right one to show ─────────────
 *
 * `src/backend/auth/errors.ts` deliberately collapses EVERY credential failure onto one
 * string, so a sign-in page cannot be used to enumerate the staff directory. That is
 * correct, and this module does not fight it. But the single string is written for the
 * EMAIL/PASSWORD step:
 *
 *   "Sign-in failed. Check your email address and password and try again. If you normally
 *    sign in with your Microsoft 365 account, use the Sign in with Microsoft button."
 *
 * The server returns that byte-for-byte when a TOTP code is wrong (verified live against
 * `POST /api/auth/login/mfa`), where it is actively misleading: the password was already
 * accepted two steps ago, and telling somebody to check it sends them back to re-enter
 * credentials that were never the problem. So at a CODE step this module substitutes a
 * message about codes — which leaks nothing, because by then the caller already holds a
 * valid step token and has therefore already proved the account exists.
 *
 * `AUTH_TOKEN_EXPIRED` gets the same treatment. Its stock message is "Your session has
 * timed out. Refreshing the page will sign you back in automatically" — untrue mid-sign-in,
 * where there is no session to refresh and the only way forward is to start again.
 *
 * Everything else — lockout, rate limiting, password policy, MFA-not-applicable — already
 * carries a specific, actionable message and is passed through untouched.
 *
 * ── On `normaliseApiError` ───────────────────────────────────────────────────
 *
 * It is not called here, and that is not an oversight: `ApiError` IS its output.
 * `toApiError` in `api/client.ts` runs every failed response through
 * `normaliseApiError(body)` and copies `message`, `code`, `issues`, `requiredPermission`
 * and `details` onto the error. Re-running it would mean re-parsing a body that has
 * already been consumed. The single-description rule is honoured by reading `ApiError`'s
 * normalised fields and never touching a raw response body.
 */
import { ApiError, isApiError } from '@frontend/api/client';

import type { LoginStep } from './loginSteps';

/** Where the failure happened. Determines which substitutions apply. */
export type AuthStage = 'CREDENTIALS' | LoginStep;

export interface AuthFailure {
  /** The banner text. Never empty. */
  message: string;
  /** Field-keyed messages, ready to hand to `FieldShell`'s `error` prop. */
  fieldErrors: Record<string, string>;
  /**
   * True when the step token is dead and the only way forward is the credentials form.
   * The caller resets to the start and says why, rather than leaving the user pressing a
   * button that can no longer succeed.
   */
  restart: boolean;
}

const CODE_REJECTED =
  'That code was not accepted. Authenticator codes change every 30 seconds — wait for the next ' +
  'one and enter it straight away rather than typing the one already on screen. If your ' +
  'authenticator is unavailable, enter one of your recovery codes instead.';

const ENROLMENT_CODE_REJECTED =
  'That code was not accepted. Check that the authenticator entry says AgentERP and shows your ' +
  'email address, then enter the code it is showing right now — they change every 30 seconds. ' +
  'If the app never scanned, use the setup key below to add the account by hand.';

const STEP_TOKEN_DEAD =
  'This sign-in took longer than ten minutes and the step expired, so nothing was saved. ' +
  'Enter your email address and password again to start over.';

const STEP_TOKEN_INVALID =
  'This sign-in could not be continued — the step it was on is no longer valid. Enter your ' +
  'email address and password again to start over.';

/** Codes that mean the step token can no longer be spent. */
const DEAD_TOKEN_CODES = new Set(['AUTH_TOKEN_EXPIRED', 'AUTH_TOKEN_INVALID', 'AUTH_SESSION_EXPIRED']);

function fieldErrorsFrom(error: ApiError): Record<string, string> {
  const grouped = new Map<string, string[]>();
  for (const issue of error.issues) {
    const key = issue.field === 'newPassword' ? 'newPassword' : issue.field;
    const existing = grouped.get(key);
    if (existing === undefined) grouped.set(key, [issue.message]);
    else existing.push(issue.message);
  }
  const flattened: Record<string, string> = {};
  for (const [field, messages] of grouped) flattened[field] = messages.join(' ');
  return flattened;
}

/**
 * @param fallback what to say when the failure carried no message at all (a proxy error
 *   page, say). Specific to the step, so it is never "something went wrong".
 */
export function describeAuthFailure(caught: unknown, stage: AuthStage, fallback: string): AuthFailure {
  if (!isApiError(caught)) {
    return { message: fallback, fieldErrors: {}, restart: false };
  }

  const code = caught.code ?? '';

  if (DEAD_TOKEN_CODES.has(code) && stage !== 'CREDENTIALS') {
    return {
      message: code === 'AUTH_TOKEN_EXPIRED' ? STEP_TOKEN_DEAD : STEP_TOKEN_INVALID,
      fieldErrors: {},
      restart: true,
    };
  }

  // The generic credential message, arriving at a step where credentials are not what
  // was being checked.
  if (code === 'AUTH_INVALID_CREDENTIALS') {
    if (stage === 'MFA_VERIFICATION') return { message: CODE_REJECTED, fieldErrors: { code: CODE_REJECTED }, restart: false };
    if (stage === 'MFA_ENROLMENT') {
      return { message: ENROLMENT_CODE_REJECTED, fieldErrors: { code: ENROLMENT_CODE_REJECTED }, restart: false };
    }
  }

  // A lockout mid-sign-in ends this attempt: the step token is still valid but every
  // remaining step will refuse it until the lockout lapses.
  if (code === 'AUTH_ACCOUNT_LOCKED') {
    return { message: caught.message, fieldErrors: {}, restart: stage !== 'CREDENTIALS' };
  }

  const fieldErrors = fieldErrorsFrom(caught);

  // The password policy check answers as a flat 400 with no `issues`, so bind it to the
  // field by hand — otherwise a rejected password shows only a banner and the input it
  // refers to is not marked.
  if (code === 'AUTH_PASSWORD_POLICY' && fieldErrors['newPassword'] === undefined) {
    fieldErrors['newPassword'] = caught.message;
  }

  return { message: caught.message, fieldErrors, restart: false };
}
